import React, { useEffect, useRef } from 'react'
import iframeResize from '@iframe-resizer/parent'
import { useModuleConfig } from '../../hooks/useModuleConfig'
import { MODULE_KEYS, FAV_CALCULATOR_DEFAULTS } from '../../moduleRegistry'
import '../../styles/FavCalculatorModule/FavCalculatorModule.scss'

/**
 * Embeds the Financial Advisor Value (FAV) calculator below the disclosure module.
 *
 * The calculator is served by the content-delivery-system (CDS) app at
 * `/external/calculators/fav`. A CloudFront Function path-routes `/external/*` to CDS
 * under the same hostname as the match page in every deployed env, so the iframe is
 * same-origin with this page — which is what lets CDS read our `data-*` attributes
 * (`window.frameElement.dataset`; cross-origin parents silently fall back to its
 * defaults). We therefore build `src` origin-relative.
 *
 * `heading`/`subheading` are flag-controllable (Optimizely variable
 * `fav_calculator_module`) and forwarded to the calculator as `data-header-text` /
 * `data-subheader-text`. `data-stylesheet-url` ships match-screen theming (hosted in
 * this repo's static dir). The contract is documented in CDS
 * `docs/features/external-calculator-embed.md`.
 *
 * `iframeResizerLicense` / `favEmbedStylesheetUrl` are webpack DefinePlugin globals
 * (see `src/matchScreen/globals.d.ts`).
 */

const CALCULATOR_PATH = '/external/calculators/fav'

export const FavCalculatorModule = () => {
  const { heading, subheading } = useModuleConfig(MODULE_KEYS.favCalculator, FAV_CALCULATOR_DEFAULTS)
  const iframeRef = useRef<HTMLIFrameElement>(null)

  useEffect(() => {
    const iframe = iframeRef.current
    if (iframe === null || typeof window === 'undefined') return

    // Build src after mount so the data-* attributes (rendered in JSX below) are present
    // before the calculator loads and reads them. hostUrl forwards the live parent URL
    // (incl. utm_*) for Mixpanel attribution — build it from window.location.href so
    // visit-time UTMs are captured.
    iframe.src = `${window.location.origin}${CALCULATOR_PATH}?hostUrl=${encodeURIComponent(window.location.href)}`

    const instances = iframeResize(
      { license: iframeResizerLicense, waitForLoad: true, log: false },
      iframe
    )

    return () => {
      instances.forEach((instance) => instance.iFrameResizer.disconnect())
    }
  }, [])

  return (
    <section className="fav-calculator-module" aria-label="Financial advisor value calculator">
      <iframe
        ref={iframeRef}
        id="fav-calculator"
        className="fav-calculator-module__iframe"
        title="Financial Advisor Value Calculator"
        data-header-text={heading}
        data-subheader-text={subheading}
        data-hide-cta="true"
        data-stylesheet-url={favEmbedStylesheetUrl}
      />
    </section>
  )
}
